import type { FormEvent } from "react";
import { useCallback, useEffect, useState } from "react";

import { Dialog } from "@/components/ui/Dialog";
import { api, type ApiKeyOut } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

interface Props {
  userId: string;
}

type KeyStatus = "active" | "expired" | "revoked";

function keyStatus(k: ApiKeyOut): KeyStatus {
  if (k.revoked_at) return "revoked";
  if (k.expires_at && new Date(k.expires_at) <= new Date()) return "expired";
  return "active";
}

function KeyStatusBadge({ status }: { status: KeyStatus }) {
  if (status === "revoked") {
    return (
      <span className="inline-block rounded border border-ink-600 bg-ink-800 px-1.5 py-0.5 text-[10px] font-medium text-ink-400">
        Revogada
      </span>
    );
  }
  if (status === "expired") {
    return (
      <span className="inline-block rounded border border-amber-800 bg-amber-900/30 px-1.5 py-0.5 text-[10px] font-medium text-amber-400">
        Expirada
      </span>
    );
  }
  return (
    <span className="inline-block rounded border border-emerald-800 bg-emerald-900/40 px-1.5 py-0.5 text-[10px] font-medium text-emerald-300">
      Ativa
    </span>
  );
}

async function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  // Fallback para ambientes sem Clipboard API (HTTP, iframes restritos)
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  document.execCommand("copy");
  document.body.removeChild(ta);
}

export function UserApiKeysPanel({ userId }: Props) {
  const [keys, setKeys] = useState<ApiKeyOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [expiresLocal, setExpiresLocal] = useState("");
  const [creating, setCreating] = useState(false);
  const [secretOut, setSecretOut] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [revokeSuccess, setRevokeSuccess] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await api.listUserApiKeys(userId);
      setKeys(list);
    } catch (err) {
      setKeys([]);
      setError(err instanceof Error ? err.message : "Erro ao carregar API keys.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const expiresAt =
        expiresLocal.trim() !== "" ? new Date(expiresLocal).toISOString() : undefined;
      const created = await api.createUserApiKey(userId, {
        name: newName.trim(),
        ...(expiresAt != null ? { expires_at: expiresAt } : {}),
      });
      setCreateOpen(false);
      setNewName("");
      setExpiresLocal("");
      setSecretOut(created.api_key);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível criar a chave.");
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (key: ApiKeyOut) => {
    if (key.revoked_at) return;
    if (!confirm(`Revogar a chave "${key.name}"? Isso não pode ser desfeito.`)) return;
    setError(null);
    setRevokeSuccess(null);
    try {
      await api.revokeUserApiKey(userId, key.id);
      setRevokeSuccess(`Chave "${key.name}" revogada com sucesso.`);
      await reload();
      setTimeout(() => setRevokeSuccess(null), 4000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível revogar.");
    }
  };

  const handleCopy = async () => {
    if (!secretOut) return;
    try {
      await copyToClipboard(secretOut);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setError("Não foi possível copiar automaticamente. Selecione o texto e copie manualmente.");
    }
  };

  return (
    <div className="mt-6 rounded-lg border border-ink-700 bg-ink-850 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-medium text-ink-200">API keys deste usuário</h2>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="rounded border border-violet-700 bg-violet-950/40 px-3 py-1.5 text-xs font-medium text-violet-200 hover:bg-violet-900/50"
        >
          Nova API key
        </button>
      </div>

      {error ? (
        <p className="mt-3 text-xs text-red-400" role="alert">
          {error}
        </p>
      ) : null}
      {revokeSuccess ? (
        <p className="mt-3 text-xs text-emerald-400" role="status">
          {revokeSuccess}
        </p>
      ) : null}

      <div className="mt-3 overflow-x-auto">
        {loading && keys.length === 0 ? (
          <p className="text-xs text-ink-500">Carregando…</p>
        ) : keys.length === 0 ? (
          <p className="text-xs text-ink-500">Nenhuma API key cadastrada.</p>
        ) : (
          <table className="w-full text-left text-xs text-ink-300">
            <thead>
              <tr className="border-b border-ink-700 text-ink-400">
                <th className="pb-2 pr-3 font-medium">Nome</th>
                <th className="pb-2 pr-3 font-medium">Prefixo</th>
                <th className="pb-2 pr-3 font-medium">Último uso</th>
                <th className="pb-2 pr-3 font-medium">Expira</th>
                <th className="pb-2 pr-3 font-medium">Status</th>
                <th className="pb-2 text-right font-medium">Ações</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => {
                const status = keyStatus(k);
                const isInactive = status === "revoked";
                return (
                  <tr key={k.id} className="border-b border-ink-800 last:border-b-0">
                    <td className="py-2 pr-3 text-ink-200">{k.name}</td>
                    <td className="py-2 pr-3 font-mono text-ink-400">{k.key_prefix}…</td>
                    <td className="py-2 pr-3">{formatDateTime(k.last_used_at)}</td>
                    <td className="py-2 pr-3">{formatDateTime(k.expires_at)}</td>
                    <td className="py-2 pr-3">
                      <KeyStatusBadge status={status} />
                    </td>
                    <td className="py-2 text-right">
                      <button
                        type="button"
                        disabled={isInactive}
                        title={
                          isInactive
                            ? "Chave já revogada."
                            : "Revogar chave permanentemente."
                        }
                        onClick={() => void handleRevoke(k)}
                        className="rounded border border-ink-600 px-2 py-1 text-[10px] font-medium hover:bg-ink-800 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        Revogar
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Dialog: nova API key */}
      <Dialog
        title="Nova API key"
        open={createOpen}
        onClose={() => !creating && setCreateOpen(false)}
        footer={
          <>
            <button
              type="button"
              disabled={creating}
              onClick={() => setCreateOpen(false)}
              className="rounded border border-ink-600 px-4 py-2 text-sm text-ink-200 hover:bg-ink-800"
            >
              Cancelar
            </button>
            <button
              type="submit"
              form="api-key-create-form"
              disabled={creating}
              className="rounded bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-500 disabled:opacity-50"
            >
              {creating ? "Criando…" : "Criar"}
            </button>
          </>
        }
      >
        <form id="api-key-create-form" className="space-y-3" onSubmit={(e) => void handleCreate(e)}>
          <div className="space-y-1">
            <label htmlFor="apikey-name" className="block text-xs text-ink-400">
              Nome da chave
            </label>
            <input
              id="apikey-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              autoFocus
              className="w-full rounded border border-ink-600 bg-ink-900 px-3 py-2 text-sm text-ink-100 outline-none ring-violet-500/40 focus:ring-2"
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="apikey-exp" className="block text-xs text-ink-400">
              Expira em (opcional)
            </label>
            <input
              id="apikey-exp"
              type="datetime-local"
              value={expiresLocal}
              onChange={(e) => setExpiresLocal(e.target.value)}
              className="w-full rounded border border-ink-600 bg-ink-900 px-3 py-2 text-sm text-ink-100 outline-none ring-violet-500/40 focus:ring-2"
            />
          </div>
          {error && creating === false ? (
            <p className="text-xs text-red-400">{error}</p>
          ) : null}
        </form>
      </Dialog>

      {/* Dialog: exibir segredo recém-criado */}
      <Dialog
        title="Copie sua API key"
        open={secretOut !== null}
        onClose={() => {
          setSecretOut(null);
          setCopied(false);
        }}
        footer={
          <button
            type="button"
            onClick={() => void handleCopy()}
            className={`rounded border px-4 py-2 text-sm font-medium transition-colors ${
              copied
                ? "border-emerald-700 bg-emerald-900/40 text-emerald-300"
                : "border-ink-600 text-ink-200 hover:bg-ink-800"
            }`}
          >
            {copied ? "Copiado!" : "Copiar"}
          </button>
        }
      >
        <p className="text-xs text-amber-400">
          Esta chave só será exibida uma vez. Guarde em um local seguro antes de fechar.
        </p>
        <pre className="mt-3 select-all overflow-x-auto break-all rounded border border-ink-600 bg-ink-950 p-3 font-mono text-[11px] text-ink-100">
          {secretOut ?? ""}
        </pre>
        {error && !creating ? (
          <p className="mt-2 text-xs text-red-400">{error}</p>
        ) : null}
      </Dialog>
    </div>
  );
}
